import { ChangeEvent, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  enableOrDisableOrder,
  getPatientCredits,
  getPatientDetails,
  setPasswordByAdmin,
  updatePatientData,
  updatePrescriptionPreferable,
} from '../service';
import CustomDatePicker from '../components/CustomDatePicker';
import Select, { SingleValue, components } from 'react-select';
import Prescriptions from './Prescriptions';
import Loading from '../components/Loading';
import {
  DeactivateIcon,
  DownCaretIcon,
  EditPatientIcon,
  NotesIcon,
  NotesIconBlue,
  PrescriptionIcon,
  TokenIcon,
  WalletIcon,
} from '../assets/svgImages';
import CustomModal from '../components/CustomModal';
import {
  Emergencycontact,
  PatientInfoState,
  ReactSelectOptionProp,
  ReactSelectOptionPropTwo,
  MonthlyLimit,
  Limit_Message_Enum,
  TabItem,
} from '../interface/interface';
import { useToHandlePrescriptionActions } from '../hooks/useToHandlePrescriptionActions';
import TextAreaWithLimit from '../components/TextArea';
import moment from 'moment';
import Toggle from '../components/ToggleComponent';
import showToast from '../components/ToastComponent';
import { Tooltip } from '../components/Tooltip';
import instance from '../config/axios';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { genders, userType } from '../constant/constant';
import isValidAustralianPostcode from 'is-valid-australian-postcode';
import { formatPrice } from '../utils/numberFormat';
import { getShippingAddress } from '../utils/formatAddress';
import ReactPasswordChecklist from 'react-password-checklist';
import { validatedPassword } from '../validator/passwordValidator';
import PatientProducts from './PatientProducts';
import PatientMonthlyLimit from './PatientMonthlyLimit';
import PatientOrders from './PatientOrders';
import Tabs from '../components/Tabs';

const PatientEdit = () => {
  const { user, removeUser } = useAuth();
  const navigateTo = useNavigate();
  const [credits, setStoreCredits] = useState<number>(0);
  const [patientDetail, setPatientDetails] = useState<
    PatientInfoState | undefined
  >();
  const { patientId: patient_id } = useParams();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  // Notes
  const [note, setNote] = useState<string>('');
  const [isNotesModalOpen, setNotesModelVisibility] = useState(false);

  const [triggerPreferable, setTriggerPreferable] = useState<{
    id: number;
    isPreferable: boolean | null;
  }>();

  const [
    prescription,
    modalActions,
    productDataParams,
    addPrescriptionModalParams,
    triggerRender,
  ] = useToHandlePrescriptionActions(
    user?.access_token as string,
    Number(patient_id as string),
  );

  const [loading, setLoading] = useState<boolean>(false);

  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);

  const [editable, setEditable] = useState(false);
  const handleEdit = () => {
    setEditable(true);
  };

  const [open, setOpen] = useState<boolean>(false);
  const [activeConfirmation, setActiveConfirmation] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(false);
  const [resetPasswordModal, setResetPasswordModal] = useState<boolean>(false);
  const [password, setPassword] = useState('');

  const [openTokenModal, setOpenTokenModal] = useState<boolean>(false);
  const [token, setToken] = useState<string>();

  const [addressEditable, setAddressEditable] = useState(false);
  const handleAddressEdit = () => {
    setAddressEditable(true);
  };

  const [shippingAddress1, setShippingAddress1] = useState<string>('');
  const [shippingAddress2, setShippingAddress2] = useState<string>('');
  const [shippingCity, setShippingCity] = useState<string>('');
  const [shippingProvince, setShippingProvince] = useState<string>('');
  const [shippingZip, setShippingZip] = useState<string>('');
  const [shippingCountry, setShippingCountry] = useState<string>('Australia');
  const [gender, setGender] = useState<ReactSelectOptionProp>();
  const [patientType, setPatientType] = useState<ReactSelectOptionPropTwo>();

  const [emergencyContacts, setEmergencyContact] = useState<Emergencycontact>();

  // Allow to order flag
  const [isAllowedToOrder, setAllowOrderFlag] = useState<boolean>();

  const [triggerAllowOrderUpdate, setTriggerAllowOrder] =
    useState<boolean>(true);

  const accessToken = user?.access_token;

  const [filter, setFilter] = useState<
    SingleValue<{
      label: string;
      value: string;
    }>
  >({ label: 'Active', value: 'active' });

  useEffect(() => {
    if (isAllowedToOrder === undefined || !user?.access_token) return;

    enableOrDisableOrder(user.access_token, {
      allowedToOrder: isAllowedToOrder,
      patientId: Number(patientDetail?.patientId),
    }).then((status) => {
      if (status === 200) {
        if (isAllowedToOrder) {
          showToast({
            message: 'Access to order enabled for this Patient',
            type: 'info',
          });
          return;
        }
        showToast({
          message: 'Access to order disabled for this Patient',
          type: 'info',
        });
      } else {
        setAllowOrderFlag((prev) => !prev);
        showToast({
          message: 'Failed to update',
          type: 'info',
        });
      }
    });
  }, [triggerAllowOrderUpdate]);

  const proceedToCreatePatient: boolean = true;

  const fetchData = () => {
    if (!patient_id || isNaN(Number(patient_id))) return navigateTo('/404');
    setLoading(true);

    getPatientDetails(
      patient_id,
      filter?.value ?? 'active',
      user?.access_token as string,
    ).then((data) => {
      if (typeof data === 'number') {
        navigateTo('/404');
        setLoading(false);
        return;
      }

      if (data?.patientDetail) {
        const { patientDetail, address } = data || {};
        const { user } = patientDetail || {};

        setPatientDetails({
          firstName: user.first_name,
          lastName: user.last_name,
          middleName: user.middle_name,
          preferredName: user.preferred_name,
          consent: patientDetail.consent,
          dob: user.dob,
          email: user.email,
          active: user.active,
          title: user.title,
          gender:
            genders.find((x) => x.value === user.gender)?.value ?? 'unknown',
          internal: !!userType.find((ut) => ut.value === patientDetail.internal)
            ?.value,
          mobile: user.phone.mobile,
          home: user.phone.home,
          medicare: patientDetail?.medicare,
          medicare_reference: patientDetail?.reference_id ?? '',
          medicareExpiry: patientDetail?.medicareExpiry,
          dva: patientDetail?.dva,
          userId: user.id.toString(),
          patientId: patientDetail.id.toString(),
          note: patientDetail.note ?? '',
          allowedToOrder: patientDetail.allowedToOrder,
          monthlyLimit: patientDetail.monthly_limit,
          vapLimit: patientDetail.vape_limit,
        });
        setAllowOrderFlag(patientDetail.allowedToOrder);

        setGender(
          genders.find((x) => x.value === user.gender) ?? {
            label: 'Unknown',
            value: 'unknown',
          },
        );

        setPatientType(
          userType.find((ut) => ut.value === patientDetail.internal),
        );

        if (address) {
          setShippingAddress1(address?.address_line_one);

          if (address?.address_line_two) {
            setShippingAddress2(address.address_line_two ?? '');
          }
          setShippingCity(address?.city);
          setShippingProvince(address?.state?.name);
          setShippingCountry(address?.state?.country?.name);
          setShippingZip(address?.post_code?.toString());
        }
        // Setting Prescriptions
        prescription.setPrescriptionList(
          patientDetail.prescriptions.map((p) => ({
            id: p?.id,
            createdDate: p?.createdDate,
            prescriedQuantity: p?.prescribed_quantity,
            prescriedRepeats: p?.prescribed_repeats,
            prescriedInterval: p?.prescribed_interval,
            isPreferable: p?.isPreferable,
            flag: false,
            product: {
              id: p.product.id,
              name: p.product.name,
              imgUrl: p.product.image_url,
              monthlyLimit: p.product.monthlyLimit,
              description: p.product.description,
              productFormulationId: p.product?.productFormulation?.id,
            },
            eRx_qty: p?.eRx_qty,
            prescribedDosage: p?.prescribed_dosage,
            comment: p.comment,
            eRxToken: p?.token,
            prescriptionCode: p?.prescription_code,
            prescriptionDate: moment(p?.prescription_date).isValid()
              ? moment(p?.prescription_date).format('DD/MM/YYYY')
              : '-',
            remainingRepeats: p.remaining_repeats,
            prescriptionExpiry: p?.prescription_expiry,
            timesPreviouslyDispensed: p.timesPreviouslyDispensed,
            totalProductsUsed: p.totalProductsused,
            status: p.status,
            remainingUnits: p.remaining_units,
            medicationForm: p?.medication_formrtacid
              ? p?.medication_formrtacid
              : '-',
            medicationStrength: p?.medication_strength
              ? p?.medication_strength
              : '-',
            drugName: p?.aip_complient_drug_name
              ? p.aip_complient_drug_name
              : '-',
            brandName: p?.medication_brand_name ? p.medication_brand_name : '-',
            routeAdmin: p?.route_administration ? p.route_administration : '-',
            doctor: p?.doctor
              ? `${p.doctor.user.first_name} ${p.doctor.user.last_name}`
              : null,
            isIntervalReached: p.isIntervalReached,
          })),
        );

        if (patientDetail?.emergency_contacts.length) {
          setEmergencyContact({
            id: patientDetail.emergency_contacts[0].id,
            contact_name: patientDetail.emergency_contacts[0].contact_name,
            contact_number: patientDetail.emergency_contacts[0].contact_number,
            contact_relationship:
              patientDetail.emergency_contacts[0].contact_relationship,
            consent: patientDetail.emergency_contacts[0].consent,
          });
        }

        // Notes
        setNote(patientDetail.note ?? '');

        //Patient Credits
        if (user.email) {
          getPatientCredits(user.email, accessToken).then((data: any) => {
            const storeCredits = data;
            setStoreCredits(storeCredits);
          });
        }
      }

      setLoading(false);
    });
  };

  useEffect(() => {
    if (addressEditable || editable) return;
    if (!patient_id || isNaN(Number(patient_id))) return navigateTo('/404');
    fetchData();
    // Call for user data retrival
  }, [patient_id, triggerRender, addressEditable, editable, filter]);

  useEffect(() => {
    const token = user?.access_token;
    if (!triggerPreferable?.id || !token) return;
    updatePrescriptionPreferable(
      triggerPreferable.id,
      !triggerPreferable.isPreferable,
      token,
    ).then((res) => {
      if (res === 200) {
        fetchData();
      }
    });
  }, [triggerPreferable]);

  useEffect(() => {
    if (!resetPasswordModal) {
      setPassword('');
    }
  }, [resetPasswordModal]);

  const handleInput = (
    e: ChangeEvent<HTMLInputElement>,
    key: 'user' | 'address' | 'emergency_contact',
  ) => {
    if (patientDetail) {
      // Handle user panel
      if (key === 'user') {
        let value: string | null = e.target.value;
        if (
          [MonthlyLimit.DRIED_LIMIT, MonthlyLimit.VAP_LIMIT].includes(
            e.target.name as MonthlyLimit,
          )
        ) {
          if (value === '' || isNaN(Number(value))) {
            value = null;
          }
        }
        setPatientDetails({
          ...patientDetail,
          [e.target.name]: value,
        });
      }

      if (key === 'emergency_contact') {
        setEmergencyContact({
          ...emergencyContacts,
          [e.target.name]: e.target.value,
        } as Emergencycontact);
      }
    }
  };

  const handleDate = (date: Date, key: string) => {
    if (patientDetail)
      setPatientDetails({
        ...patientDetail,
        [key]: date,
      });
  };

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    target: 'overall' | 'address' | 'note' | 'emergency_contact',
  ) => {
    try {
      e.preventDefault();
      if (!patientDetail) return;

      if (
        patientDetail?.monthlyLimit &&
        (patientDetail?.monthlyLimit > MonthlyLimit.MAX_DRIED_LIMIT ||
          patientDetail?.monthlyLimit < MonthlyLimit.MIN_DRIED_LIMIT)
      ) {
        showToast({
          type: 'info',
          message: Limit_Message_Enum.DRIED_LIMIT_MESSAGE,
        });
        return;
      }

      if (
        patientDetail?.vapLimit &&
        (patientDetail?.vapLimit > MonthlyLimit.MAX_VAP_LIMIT ||
          patientDetail?.vapLimit < MonthlyLimit.MIN_VAP_LIMIT)
      ) {
        showToast({
          type: 'info',
          message: Limit_Message_Enum.VAP_LIMIT_MESSAGE,
        });
        return;
      }
      const validateZipCode = isValidAustralianPostcode(shippingZip);
      if (!validateZipCode) {
        showToast({
          message: 'Please Enter Valid Postcode',
          type: 'warning',
        });
        return;
      }

      let dataToUpdate;

      if (target === 'overall') {
        const stringifiedPayload = JSON.stringify(patientDetail);
        const parsedPatientData = JSON.parse(stringifiedPayload);
        dataToUpdate = {
          userData: {
            title: parsedPatientData.title,
            firstName: parsedPatientData.firstName,
            lastName: patientDetail.lastName,
            middleName: patientDetail.middleName,
            preferredName: patientDetail.preferredName,

            dob: patientDetail.dob
              ? moment(patientDetail.dob).format('YYYY-MM-DDTHH:mm:ss.SSS') +
                'Z'
              : '-',
            email: patientDetail.email,
            gender: patientDetail.gender,
            medicare: patientDetail.medicare,
            medicare_reference: patientDetail.medicare_reference,
            medicareExpiry: patientDetail.medicareExpiry
              ? moment(patientDetail.medicareExpiry).format(
                  'YYYY-MM-DDTHH:mm:ss.SSS',
                ) + 'Z'
              : '-',
            dva: patientDetail.dva,
            mobile: patientDetail.mobile,
            home: patientDetail.home,
            internal: patientDetail.internal,
            monthlyLimit: patientDetail.monthlyLimit,
            vapLimit: patientDetail?.vapLimit,
          },
          userId: patientDetail.userId,
          patientId: patientDetail.patientId,
          needMail: false,
          note,
        };
      }

      if (target === 'address') {
        dataToUpdate = {
          address: {
            address_line_one: shippingAddress1,
            address_line_two: shippingAddress2,
            street_name: '',
            street_number: '',
            city: shippingCity,
            state: shippingProvince,
            post_code: shippingZip,
            country: shippingCountry,
          },
          emergency_contact_information: {
            ...emergencyContacts,
          },
          userId: patientDetail.userId,
          patientId: patientDetail.patientId,
          userData: {
            mobile: patientDetail.mobile,
            home: patientDetail.home,
            email: patientDetail.email,
            internal: patientDetail.internal,
            firstName: patientDetail?.firstName,
            lastName: patientDetail?.lastName,
          },
          needMail: true,
          note,
        };
      }

      if (target === 'note') {
        dataToUpdate = {
          userId: patientDetail.userId,
          patientId: patientDetail.patientId,
          note,
          userData: {
            mobile: patientDetail.mobile,
            home: patientDetail.home,
            email: patientDetail.email,
            firstName: patientDetail?.firstName,
            lastName: patientDetail?.lastName,
            internal: patientDetail.internal,
          },
          needMail: false,
        };
      }

      if (!dataToUpdate) return;

      const status = await updatePatientData(
        user?.access_token as string,
        dataToUpdate,
      );

      if (status === 200) {
        showToast({
          message: 'Patient info updated successfully.',
          type: 'success',
        });
        fetchData();

        if (target === 'note') {
          if (patientDetail)
            setPatientDetails((p) => ({ ...(p as PatientInfoState), note }));
        }
      } else {
        showToast({
          message: 'Unable to update Patient info',
          type: 'info',
        });
      }

      if (target === 'note') setNotesModelVisibility(false);
      setEditable((p) => p && !p);
      setAddressEditable((p) => p && !p);
    } catch (e) {
      console.log('Unable to update patient data', e);
    }
  };

  const deactivePatient = async () => {
    const response = await instance.put(
      '/api/v1/patient/deactivate/admin',
      {
        id: Number(patient_id as string),
      },
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      },
    );

    if (response.status === 200) {
      showToast({
        message: 'Patient User Deactivate Successfully',
        type: 'success',
      });
      navigateTo('/patients');
      return;
    }
    showToast({
      message: 'Failed to Deactivate Patient',
      type: 'warning',
    });
  };

  const activatePatient = async () => {
    const response = await instance.put(
      '/api/v1/patient/activate/admin',
      {
        id: Number(patient_id as string),
      },
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
      },
    );

    if (response.status === 200) {
      showToast({
        message: 'Patient User Activated Successfully',
        type: 'success',
      });
      navigateTo('/patients');
      return;
    }
    showToast({
      message: 'Failed to Activated Patient',
      type: 'warning',
    });
  };

  const submitToken = async () => {
    await instance
      .post(
        '/api/v1/erxToken/addToken',
        {
          token,
          proceedToCreatePatient,
        },
        {
          headers: {
            Authorization: `Bearer ${user?.access_token}`,
          },
        },
      )
      .then((res) => {
        if (res.status === 200) {
          const message =
            res.data.message ?? 'Token added to patient successfully';
          showToast({
            message: message,
            type: 'success',
          });
          // proceedToCreatePatient = false;
          setOpenTokenModal(false);
        } else if (res.status === 405) {
          // setErxConfirmationModal(true);
          showToast({
            message: 'Patient Not found',
            type: 'warning',
          });
        } else {
          showToast({
            message: res.data.message,
            type: 'warning',
          });
        }
      })
      .catch((err) => {
        console.error(err);
        showToast({
          message: 'Failed to add token to Patient. Please try again later',
          type: 'error',
        });
        setOpenTokenModal(false);
      });
  };

  const resetPassword = async () => {
    if (!user?.access_token) {
      showToast({
        type: 'warning',
        message: 'Access Token not found',
      });
      return;
    }

    setPasswordByAdmin(
      password,
      Number(patient_id as string),
      user.access_token,
    )
      .then((res) => {
        showToast({
          type: res.status === 200 ? 'success' : 'warning',
          message: res.data.message,
        });
      })
      .catch((err) => {
        console.log(err);
        showToast({
          message: 'Failed to Update Password',
          type: 'error',
        });
      });
    setResetPasswordModal(false);
  };

  if (loadingStatus) {
    return <Loading />;
  }

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <DownCaretIcon />
      </components.DropdownIndicator>
    );
  };

  const tabs: TabItem[] = [
    {
      id: 'tab1',
      label: 'Medications',
      component: (
        <Prescriptions
          prescription={{
            data: prescription.prescriptionList,
            setter: (ele) => prescription.setPrescriptionList([...ele]),
            ...modalActions,
          }}
          doctorAndProducts={productDataParams}
          addPrescriptionModalParams={addPrescriptionModalParams}
          setTriggerPreferable={setTriggerPreferable}
          filter={{
            state: filter,
            setter: setFilter,
          }}
        />
      ),
    },
    {
      id: 'tab2',
      label: 'Orders',
      component: <PatientOrders />,
    },
    {
      id: 'tab3',
      label: 'Products',
      component: <PatientProducts />,
    },
    {
      id: 'tab4',
      label: 'Monthly Limits',
      component: <PatientMonthlyLimit />,
    },
  ];

  return (
    <>
      {!loading ? (
        <>
          <button
            className="bg-primary-blue-500 text-white text-base font-semibold pl-8 pr-4 
        rounded-l-full py-3 fixed right-0 top-28 3xl:top-48 flex items-center"
            onClick={() => setNotesModelVisibility(true)}
          >
            <NotesIcon />
            <span className="ml-2">Add note</span>
          </button>
          <div className="flex items-center text-sm text-primary-blue-500 font-regular mb-4">
            <a className="mr-3" href="/patients">
              Patients
            </a>
            <p>
              <span className="mr-3">/</span>
              <span>Patient Profile</span>
            </p>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-2xl text-primary-blue-500 font-semibold border-b-4 border-secondary-pink-500 w-max">
                {patientDetail?.firstName + ' ' + patientDetail?.lastName}
              </h1>
              <div className="bg-secondary-tangerine-50 px-4 py-3 rounded-md ml-10 flex">
                <h4 className="text-base text-primary-blue-500 font-semibold mr-3">
                  Credit balance
                </h4>
                <div className="flex items-center">
                  <WalletIcon />
                  <span className="ml-2 text-base font-regular text-primary-blue-500">
                    ${formatPrice(credits.toString()) || '0'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <div className="mr-10">
                <Toggle
                  label="Consent"
                  value={!!patientDetail?.consent}
                  onToggle={() => {}}
                  readonly={true}
                />
              </div>

              <Toggle
                label="Access to order"
                value={isAllowedToOrder as boolean}
                onToggle={(val: boolean) => {
                  setAllowOrderFlag(val);
                  setTriggerAllowOrder((p) => !p);
                }}
                readonly={false}
              />
              <div
                className="relative ml-3 cursor-pointer"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                {showTooltip && (
                  <Tooltip
                    className="right-0"
                    text={
                      isAllowedToOrder
                        ? 'Disabling this option will restrict a patient from placing orders.'
                        : 'Enabling this option will allow a patient to resume placing orders.'
                    }
                  />
                )}
                <i
                  className="fa fa-question-circle-o text-primary-blue-500"
                  aria-hidden="true"
                ></i>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-4 mt-8">
            <div className="col-span-4 bg-secondary-pink-50 p-6 rounded-lg">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-primary-blue-500 font-semibold">
                  Personal Information
                </h2>

                {editable ? (
                  <div className="flex items-center">
                    <button
                      onClick={() => setEditable(false)}
                      className="mr-6 text-secondary-darkred-500 text-sm font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={(e) => {
                        handleSubmit(e, 'overall');
                      }}
                      className="bg-secondary-green-600 text-white rounded-md px-5 py-3"
                    >
                      Save Changes
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleEdit}
                    className="border-2 rounded-md border-secondary-pink-600 px-6 py-3 text-secondary-pink-600
                     text-sm font-semibold flex items-center"
                  >
                    <EditPatientIcon />
                    <span className="ml-2">Edit Profile</span>
                  </button>
                )}
              </div>
              <div className="bg-white px-5 py-8 rounded-md flex mt-6 flex-wrap">
                <div className="basis-1/3 mb-10">
                  <label
                    htmlFor="first-name"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    First name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    id="first-name"
                    autoComplete="given-name"
                    className={`w-11/12 text-base font-regular text-primary-blue-500
                 ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                    value={patientDetail?.firstName}
                    onChange={(e) => handleInput(e, 'user')}
                    readOnly={!editable}
                  />
                </div>
                <div className="basis-1/3 mb-10">
                  <label
                    htmlFor="middle-name"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Middle Name
                  </label>
                  <input
                    type="text"
                    name="middleName"
                    id="middle-name"
                    autoComplete="given-name"
                    className={`w-11/12 text-base font-regular text-primary-blue-500 
                ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                    value={patientDetail?.middleName}
                    onChange={(e) => handleInput(e, 'user')}
                    readOnly={!editable}
                  />
                </div>
                <div className="basis-1/3 mb-10">
                  <label
                    htmlFor="last-name"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Last name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    id="last-name"
                    autoComplete="family-name"
                    className={`w-11/12 text-base font-regular text-primary-blue-500 
                ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                    value={patientDetail?.lastName}
                    onChange={(e) => handleInput(e, 'user')}
                    readOnly={!editable}
                  />
                </div>
                <div className="basis-1/3 mb-10">
                  <label
                    htmlFor="prefer-name"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Preferred name
                  </label>
                  <input
                    type="text"
                    name="preferredName"
                    id="prefer-name"
                    autoComplete="prefer-name"
                    className={`w-11/12 text-base font-regular text-primary-blue-500 
                ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                    value={patientDetail?.preferredName}
                    onChange={(e) => handleInput(e, 'user')}
                    readOnly={!editable}
                  />
                </div>
                <div className="basis-1/3 mb-10">
                  <label className="text-xl text-secondary-pink-700 font-semibold">
                    Date Of Birth
                  </label>
                  <div
                    className={`relative max-w-sm custom-date-picker 
                ${editable ? 'editable-datepicker' : 'uneditable-datepicker'}`}
                  >
                    {moment(patientDetail?.dob).isValid() ? (
                      <CustomDatePicker
                        value={
                          new Date(
                            moment(patientDetail?.dob as string).toDate(),
                          )
                        }
                        handleDate={(date: Date) => handleDate(date, 'dob')}
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
                <div className="basis-1/3 mb-10 patient-custom-select">
                  <label
                    htmlFor=""
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Gender
                  </label>
                  <Select
                    value={gender}
                    options={genders as ReactSelectOptionProp[]}
                    onChange={(selectedOption) => {
                      setGender(selectedOption as ReactSelectOptionProp);
                      setPatientDetails({
                        ...patientDetail,
                        gender: selectedOption!.value,
                      } as PatientInfoState);
                    }}
                    isSearchable={false}
                    components={{
                      DropdownIndicator,
                      IndicatorSeparator: () => null,
                    }}
                    isDisabled={!editable}
                    className={!editable ? 'gender' : ''}
                  />
                </div>
                <div className="basis-1/3 mb-10">
                  <label
                    htmlFor="medicare_reference"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Medicare No
                  </label>
                  <div className="">
                    <input
                      type="number"
                      name="medicare"
                      id="medicare"
                      autoComplete="address-level1"
                      className={`w-8/12 text-base font-regular text-primary-blue-500 max-w-32
                  ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                      value={patientDetail?.medicare}
                      onChange={(e) => handleInput(e, 'user')}
                      readOnly={!editable}
                    />
                    <input
                      type="number"
                      name="medicare_reference"
                      id="medicare_reference"
                      autoComplete="address-level1"
                      className={`w-2/12 text-base font-regular text-primary-blue-500 ml-2 max-w-12
                  ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                      value={patientDetail?.medicare_reference}
                      onChange={(e) => handleInput(e, 'user')}
                      readOnly={!editable}
                    />
                  </div>
                </div>
                <div className="basis-1/3 mb-10">
                  <label className="text-xl text-secondary-pink-700 font-semibold">
                    Medicare Expiry
                  </label>
                  <div
                    className={`relative max-w-sm custom-date-picker 
                ${editable ? 'editable-datepicker' : 'uneditable-datepicker'}`}
                  >
                    {moment(patientDetail?.medicareExpiry).isValid() ? (
                      <CustomDatePicker
                        value={
                          patientDetail?.medicareExpiry
                            ? new Date(
                                moment(
                                  patientDetail?.medicareExpiry as string,
                                ).toDate(),
                              )
                            : undefined
                        }
                        handleDate={(date: Date) =>
                          handleDate(date, 'medicareExpiry')
                        }
                        minDate
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
                <div className="basis-1/3 mb-10">
                  <label
                    htmlFor="dva"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    DVA No
                  </label>
                  <input
                    type="number"
                    name="dva"
                    id="dva"
                    autoComplete="address-level1"
                    className={`w-11/12 text-base font-regular text-primary-blue-500 
                ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                    value={patientDetail?.dva}
                    onChange={(e) => handleInput(e, 'user')}
                    readOnly={!editable}
                  />
                </div>

                <div className="basis-1/3 mb-10 patient-custom-select">
                  <label
                    htmlFor=""
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Patient Type
                  </label>
                  <Select
                    value={patientType}
                    options={userType as ReactSelectOptionPropTwo[]}
                    onChange={(selectedOption) => {
                      setPatientType(
                        selectedOption as ReactSelectOptionPropTwo,
                      );
                      setPatientDetails({
                        ...patientDetail,
                        internal: selectedOption!.value,
                      } as PatientInfoState);
                    }}
                    isSearchable={false}
                    components={{
                      DropdownIndicator,
                      IndicatorSeparator: () => null,
                    }}
                    isDisabled={!editable}
                    className={!editable ? 'gender' : ''}
                  />
                </div>

                <div className="basis-1/3 mb-10">
                  <label
                    htmlFor="monthly_limit"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Dried Monthly Limit
                  </label>
                  <input
                    type="number"
                    name="monthlyLimit"
                    id="monthly_limit"
                    className={`w-11/12 text-base font-regular text-primary-blue-500 
                ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                    value={patientDetail?.monthlyLimit as number}
                    onChange={(e) => handleInput(e, 'user')}
                    readOnly={!editable}
                  />
                </div>

                <div className="basis-1/3 mb-10">
                  <label
                    htmlFor="monthly_limit"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Vape Monthly Limit
                  </label>
                  <input
                    type="number"
                    name="vapLimit"
                    id="vap_limit"
                    className={`w-11/12 text-base font-regular text-primary-blue-500 
                ${editable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                    value={patientDetail?.vapLimit as number}
                    onChange={(e) => handleInput(e, 'user')}
                    readOnly={!editable}
                  />
                </div>
              </div>
            </div>
            <div className="col-span-1">
              <button
                className="bg-white rounded-md border-2 border-secondary-pink-600 text-secondary-pink-600 text-base 
                font-semibold px-8 py-3 w-full flex mb-5 items-center"
                onClick={async () => {
                  setLoadingStatus(true);
                  instance({
                    method: 'GET',
                    url: `/api/v1/user/impersonate/${Number(patient_id)}`,
                    headers: {
                      Authorization: `Bearer ${user?.access_token}`,
                    },
                    withCredentials: true,
                  })
                    .then((resp) => {
                      console.log('D> impersonate response: ', resp);
                      if (resp.data?.impersonateJwt) {
                        localStorage.setItem(
                          'impersonateUser',
                          JSON.stringify({
                            access_token: resp.data?.impersonateJwt,
                          }),
                        );
                        // dispatch?
                        removeUser();
                        setLoadingStatus(false);
                        navigateTo('/home');
                      }
                    })
                    .catch((e: unknown) => {
                      setLoadingStatus(false);
                      console.error('D> error fetching patients: ', e);
                    });
                }}
              >
                <EditPatientIcon />
                <span className="ml-3">Impersonate</span>
              </button>
              <button
                className="bg-primary-blue-500 rounded-md text-white text-base font-semibold px-8 py-3 w-full flex"
                onClick={() => modalActions.handleModal(true)}
              >
                <PrescriptionIcon />
                <span className="ml-3">Add Prescription</span>
              </button>

              <button
                className="bg-primary-red-100 rounded-md text-primary-blue-500 text-base font-semibold px-8 py-3 ml-auto w-full flex mb-5 mt-5"
                onClick={() => {
                  setToken(undefined);
                  setOpenTokenModal(true);
                }}
              >
                <TokenIcon />
                <span className="ml-3">Add Token</span>
              </button>

              <div className="bg-white w-full px-6 py-5 mt-3 rounded-md patient-notes shadow-sm">
                <h3 className="text-primary-blue-500 text-xl font-semibold flex items-center">
                  <NotesIconBlue />
                  <span className="ml-2.5">Notes</span>
                </h3>
                <div className="notes-content mt-2">{patientDetail?.note}</div>
              </div>
            </div>
          </div>

          <div className="mt-6 w-full bg-secondary-pink-50 p-5 rounded-md">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl text-primary-blue-500 font-semibold">
                Contact Information
              </h2>
              {addressEditable ? (
                <div className="flex items-center">
                  <button
                    onClick={() => setAddressEditable(false)}
                    className="mr-6 text-secondary-darkred-500 text-sm font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => {
                      handleSubmit(e, 'address');
                    }}
                    className="bg-secondary-green-600 text-white rounded-md px-5 py-3"
                  >
                    Save Changes
                  </button>
                </div>
              ) : (
                <button
                  className="border-2 rounded-md border-secondary-pink-600 px-6 py-3 
                    text-secondary-pink-600 text-sm font-semibold flex items-center"
                  onClick={handleAddressEdit}
                >
                  <EditPatientIcon />
                  <span className="ml-2">Update Address</span>
                </button>
              )}
            </div>
            <div className="bg-white px-5 py-8 rounded-md  mt-6 ">
              <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-6 mb-6">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="email"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className={`w-11/12 text-base font-regular text-primary-blue-500
                    ${addressEditable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                    onChange={(e) => handleInput(e, 'user')}
                    value={patientDetail?.email}
                    readOnly={!addressEditable}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="mobile"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Mobile
                  </label>
                  <input
                    type="number"
                    name="mobile"
                    id="mobile"
                    className={`w-11/12 text-base font-regular text-primary-blue-500 
              ${addressEditable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                    onChange={(e) => handleInput(e, 'user')}
                    value={patientDetail?.mobile}
                    readOnly={!addressEditable}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="home"
                    className="text-xl text-secondary-pink-700 font-semibold"
                  >
                    Home
                  </label>
                  <input
                    type="number"
                    name="home"
                    id="home"
                    className={`w-11/12 text-base font-regular text-primary-blue-500 
              ${addressEditable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                    onChange={(e) => handleInput(e, 'user')}
                    value={patientDetail?.home}
                    readOnly={!addressEditable}
                  />
                </div>
              </div>
              <div className="register_patient">
                <AddressAutocomplete
                  addressRefValue={shippingAddress1}
                  address1ManualSetter={(address1) =>
                    setShippingAddress1(address1)
                  }
                  state={{
                    city: shippingCity,
                    country: shippingCountry,
                    zip: shippingZip,
                    state: shippingProvince,
                    address2: shippingAddress2 ?? '',
                    streetNumber: '',
                    streetName: '',
                    unit: '',
                  }}
                  setter={(data) => {
                    setShippingCity(data.city);
                    setShippingCountry(data.country);
                    setShippingZip(data.zip);
                    setShippingProvince(data.state);
                    setShippingAddress2(data.address2 ?? '');
                    if (process.env.VITE_MAPS_AUTOCOMPLETE_KEY)
                      setShippingAddress1(
                        data?.streetName
                          ? getShippingAddress(data)
                          : shippingAddress1,
                      );
                  }}
                  freezeAddressFields={!addressEditable}
                />
              </div>
            </div>
            <h3 className="text-xl text-primary-blue-500 font-semibold mt-6">
              Consent to Contact
            </h3>
            <div className="bg-white px-5 py-4 rounded-md  mt-3 mb-5 ">
              <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-3 mt-5">
                <div className="sm:col-span-1">
                  <label
                    htmlFor="contact-name"
                    className="text-lg font-semibold  text-secondary-pink-700"
                  >
                    Contact Name
                  </label>
                  <input
                    type="text"
                    name="contact_name"
                    id="contact-name"
                    autoComplete="given-name"
                    className={`rounded-md rounded-r-md placeholder:text-gray-400 text-base
                          font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full ${addressEditable ? '' : 'border-0'}`}
                    value={emergencyContacts?.contact_name ?? '-'}
                    onChange={(e) => handleInput(e, 'emergency_contact')}
                    readOnly={!addressEditable}
                  />
                </div>
                <div className="sm:col-span-1">
                  <label
                    htmlFor="contact-phone"
                    className="text-lg font-semibold  text-secondary-pink-700"
                  >
                    Contact Phone
                  </label>

                  <input
                    type="number"
                    name="contact_number"
                    id="conatact-phone"
                    className={`rounded-md rounded-r-md placeholder:text-gray-400 text-base
                          font-regular border border-secondary-cornflowerblue-400 rounded-md p-2 w-full ${addressEditable ? '' : 'border-0'}`}
                    onChange={(e) => handleInput(e, 'emergency_contact')}
                    value={emergencyContacts?.contact_number ?? '-'}
                    readOnly={!addressEditable}
                  />
                </div>

                <div className="sm:col-span-1">
                  <label
                    htmlFor="contact-relationship"
                    className="text-lg font-semibold  text-secondary-pink-700"
                  >
                    Relationship to Patient
                  </label>
                  <div className="">
                    <input
                      type="text"
                      name="contact_relationship"
                      id="contact-relationship"
                      autoComplete="given-name"
                      className={`w-11/12 text-base font-regular text-primary-blue-500 
                        ${addressEditable ? 'border border-secondary-cornflowerblue-400 rounded-md' : 'border-0 p-0'}`}
                      value={emergencyContacts?.contact_relationship ?? '-'}
                      onChange={(e) => handleInput(e, 'emergency_contact')}
                      readOnly={!addressEditable}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2 mt-3">
                  <input
                    id="comments"
                    aria-describedby="comments-description"
                    name="consent"
                    type="checkbox"
                    className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 
                      ${addressEditable ? '' : 'opacity-50 cursor-not-allowed'}`}
                    checked={!!emergencyContacts?.consent}
                    onChange={() => {
                      if (addressEditable) {
                        setEmergencyContact({
                          ...emergencyContacts,
                          consent: !emergencyContacts?.consent,
                        } as Emergencycontact);
                      }
                    }}
                  />
                  <small className="ml-3 text-1xl text-primary-blue-500">
                    The patient consents for their Emergency Contact to speak on
                    their behalf
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 w-full bg-secondary-pink-50 px-5 rounded-md pb-5">
            <Tabs tabs={tabs} />
          </div>

          <div className="flex flex-wrap items-center justify-end">
            {(user?.profile.groups as string[]).includes('Admin') ||
            (user?.profile.groups as string[]).includes('PLO') ? (
              <button
                className="bg-white flex font-semibold text-sm text-primary-blue-400 border-2 
                    rounded-md px-6 py-3 border-primary-blue-400 my-4 mr-2"
                onClick={() => setResetPasswordModal(true)}
              >
                <EditPatientIcon />
                <span className="ml-4">Reset Password</span>
              </button>
            ) : (
              <></>
            )}

            {(user?.profile.groups as string[]).includes('Admin') ||
            (user?.profile.groups as string[]).includes('PLO') ? (
              patientDetail?.active ? (
                <button
                  className="bg-white  flex font-semibold text-sm text-primary-red-400  border-2 
                    rounded-md px-6 py-3 border-primary-red-400 my-4"
                  onClick={() => setOpen(true)}
                >
                  <DeactivateIcon />{' '}
                  <span className="ml-4">Deactivate Account</span>
                </button>
              ) : (
                <button
                  className="bg-white flex font-semibold text-sm text-primary-blue-400  border-2 
                    rounded-md px-6 py-3 border-primary-blue-400 my-4"
                  onClick={() => setActiveConfirmation(true)}
                >
                  <EditPatientIcon />
                  <span className="ml-4">Activate Account</span>
                </button>
              )
            ) : (
              <></>
            )}
          </div>
        </>
      ) : (
        <Loading />
      )}

      <CustomModal
        isOpen={modalActions?.modalState}
        onClose={() => modalActions?.handleModal(false)}
        header={'Add Prescription'}
        footer={
          <>
            <button
              className="mr-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
              onClick={(e) => addPrescriptionModalParams.handleSubmit(e)}
            >
              Create
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
              onClick={() => modalActions?.handleModal(false)}
            >
              Cancel
            </button>
          </>
        }
      >
        <div className="add-presc-container">
          {addPrescriptionModalParams?.item?.length > 0 &&
            addPrescriptionModalParams?.item?.map((pres) => (
              <div className="relative" key={pres.id}>
                <div
                  className="grid w-full grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 shadow p-5 mt-5 rounded-lg"
                  key={pres.id}
                >
                  <div className="sm:col-span-2">
                    <label
                      htmlFor={`created-date-${pres.id}`}
                      className="text-sm font-semibold text-primary-blue-500"
                    >
                      Created Date
                    </label>
                    <div className="mt-2">
                      <div className="relative max-w-sm custom-date-picker editable-datepicker create-presc-datepicker">
                        <CustomDatePicker
                          value={pres.created_date}
                          handleDate={(date) =>
                            addPrescriptionModalParams.handleDate(date, pres.id)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor={`eRxToken-${pres}`}
                      className="text-sm font-semibold text-primary-blue-500"
                    >
                      eRx Token
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        name="eRxToken"
                        id={`eRxToken-${pres.id}`}
                        autoComplete="address-level1"
                        className="block w-full rounded-md border border-secondary-cornflowerblue-400 py-1.5 text-gray-900 
                                    shadow-sm placeholder:text-gray-400"
                        value={pres.eRxToken}
                        onChange={(e) =>
                          addPrescriptionModalParams.handleInput(e, pres.id)
                        }
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor={`doctor-${pres.id}`}
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Doctor
                    </label>
                    <div className="mt-2 custom-select">
                      <Select
                        isLoading={productDataParams.isLoading}
                        value={pres.doctor}
                        options={productDataParams.doctorList}
                        onChange={(selectedOption) =>
                          addPrescriptionModalParams.handleSelect(
                            selectedOption,
                            'doctor',
                            pres.id,
                          )
                        }
                        isSearchable={true}
                        className="editable-select editable-select-full"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor={`quantity-${pres.id}`}
                      className="text-sm font-semibold text-primary-blue-500"
                    >
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        type="number"
                        name="quantity"
                        id={`quantity-${pres.id}`}
                        autoComplete="address-level1"
                        className="block w-full rounded-md border border-secondary-cornflowerblue-400 py-1.5 text-gray-900 
                                    shadow-sm placeholder:text-gray-400"
                        value={pres.quantity}
                        onChange={(e) =>
                          addPrescriptionModalParams.handleInput(e, pres.id)
                        }
                        placeholder="2"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor={`repeats-${pres.id}`}
                      className="text-sm font-semibold text-primary-blue-500"
                    >
                      Repeats <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        type="number"
                        name="repeats"
                        id={`repeats-${pres.id}`}
                        autoComplete="address-level1"
                        className="block w-full rounded-md border border-secondary-cornflowerblue-400 py-1.5 text-gray-900 
                                    shadow-sm placeholder:text-gray-400"
                        value={pres.repeats}
                        onChange={(e) =>
                          addPrescriptionModalParams.handleInput(e, pres.id)
                        }
                        placeholder="5"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor={`interval-${pres.id}`}
                      className="text-sm font-semibold text-primary-blue-500"
                    >
                      Interval
                    </label>
                    <div className="mt-2">
                      <input
                        type="number"
                        name="interval"
                        id={`interval-${pres.id}`}
                        autoComplete="address-level1"
                        className="block w-full rounded-md border border-secondary-cornflowerblue-400 py-1.5 text-gray-900 
                                    shadow-sm placeholder:text-gray-400"
                        value={pres.interval}
                        onChange={(e) =>
                          addPrescriptionModalParams.handleInput(e, pres.id)
                        }
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor={`product-${pres.id}`}
                      className="text-sm font-semibold text-primary-blue-500"
                    >
                      Product <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2 custom-select">
                      <Select
                        isLoading={productDataParams.isLoading}
                        value={pres.product}
                        options={productDataParams.productList}
                        onChange={(selectedOption) =>
                          addPrescriptionModalParams.handleSelect(
                            selectedOption,
                            'product',
                            pres.id,
                          )
                        }
                        isSearchable={true}
                        menuPlacement="top"
                        className="editable-select editable-select-full"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label
                      htmlFor={`dosage-${pres.id}`}
                      className="text-sm font-semibold text-primary-blue-500"
                    >
                      Dosage
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        name="dosage"
                        id={`dosage-${pres.id}`}
                        autoComplete="address-level1"
                        className="block w-full rounded-md border border-secondary-cornflowerblue-400 py-1.5 text-gray-900 
                                    shadow-sm placeholder:text-gray-400"
                        value={pres.dosage}
                        onChange={(e) =>
                          addPrescriptionModalParams.handleInput(e, pres.id)
                        }
                      />
                    </div>
                  </div>
                </div>
                {addPrescriptionModalParams?.item?.length > 1 ? (
                  <p
                    className="text-right text-sky-700 cursor-pointer absolute right-3 -top-3"
                    onClick={() =>
                      addPrescriptionModalParams.removePrescription(pres.id)
                    }
                  >
                    {' '}
                    <span className="text-5xl">-</span>
                  </p>
                ) : null}
              </div>
            ))}
          <div className="flex justify-end">
            <button
              onClick={() => addPrescriptionModalParams.addPrescription()}
              className="text-primary-blue-500 text-sm font-semibold px-3 py-1.5 mt-2"
            >
              + Add more
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={open}
        onClose={() => setOpen(false)}
        header="Deactivate account"
        footer={
          <div className="flex">
            <button
              className="text-sm px-3 py-2 bg-primary-red-500 border border-primary-red-500 text-white rounded-md"
              onClick={deactivePatient}
            >
              Deactivate
            </button>
            <button
              onClick={() => setOpen(false)}
              className="text-sm px-3 py-2 bg-white border border-primary-blue-500 text-primary-blue-500 rounded-md ml-3"
            >
              Cancel
            </button>
          </div>
        }
        modalSize="w-10/12 md:w-5/12 3xl:w-3/12"
      >
        <p className="text-sm text-gray-500">
          Are you sure you want to deactivate your account? All of your data
          will be permanently removed from our servers forever. This action
          cannot be undone.
        </p>
      </CustomModal>

      <CustomModal
        isOpen={activeConfirmation}
        onClose={() => setActiveConfirmation(false)}
        header="Activate account"
        footer={
          <div className="flex">
            <button
              className="text-sm px-3 py-2 bg-primary-blue-500 border border-primary-blue-500 text-white rounded-md"
              onClick={activatePatient}
            >
              Activate
            </button>
            <button
              onClick={() => setActiveConfirmation(false)}
              className="text-sm px-3 py-2 bg-white border border-primary-blue-500 text-primary-blue-500 rounded-md ml-3"
            >
              Cancel
            </button>
          </div>
        }
        modalSize="w-10/12 md:w-5/12 3xl:w-3/12"
      >
        <p className="text-sm text-gray-500">
          Are you sure you want to Activate your account?
        </p>
      </CustomModal>

      <CustomModal
        isOpen={isNotesModalOpen}
        onClose={() => setNotesModelVisibility(false)}
        header="Add Note"
        footer={
          <div className="flex">
            <button
              className="text-sm px-3 py-2 bg-primary-blue-500 border border-primary-blue-500 text-white rounded-md"
              onClick={(e) => handleSubmit(e, 'note')}
            >
              Save
            </button>
            <button
              onClick={() => setNotesModelVisibility(false)}
              className="text-sm px-3 py-2 bg-white border border-primary-blue-500 text-primary-blue-500 rounded-md ml-3"
            >
              Cancel
            </button>
          </div>
        }
        modalSize="w-10/12 md:w-5/12 3xl:w-3/12"
      >
        <div className="flex flex-col">
          <label className="text-sm text-primary-blue-500 font-semibold mb-4">
            Please add your note below:
          </label>
          <TextAreaWithLimit
            maxLength={256}
            text={note}
            onTextChange={(text: string) => {
              setNote(text);
            }}
          />
        </div>
      </CustomModal>

      <CustomModal
        isOpen={openTokenModal}
        onClose={() => setOpenTokenModal(false)}
        header={'Add Token to Patient'}
        footer={
          <>
            <button
              className="mr-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
              onClick={async () => await submitToken()}
            >
              Submit
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
              onClick={() => setOpenTokenModal(false)}
            >
              Cancel
            </button>
          </>
        }
        modalSize={'w-3/12'}
      >
        <p>Token</p>
        <div className="mt-2">
          <input
            type="text"
            name="reason"
            id="reason"
            autoComplete="given-name"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300
            placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            value={token}
            onChange={(e) => setToken(e?.target?.value)}
          />
        </div>
      </CustomModal>

      <CustomModal
        isOpen={resetPasswordModal}
        onClose={() => setResetPasswordModal(false)}
        header="Reset Password"
        footer={
          <div>
            <button
              type="button"
              className={` ${!validatedPassword(password) && 'opacity-50'}
                    mt-10 rounded-md bg-secondary-cornflowerblue-600 px-10 py-2 text-center text-sm font-semibold text-white`}
              onClick={() => resetPassword()}
              disabled={!validatedPassword(password)}
            >
              Set Password
            </button>
            <button
              onClick={() => setResetPasswordModal(false)}
              className="text-sm px-3 py-2 bg-white border border-primary-blue-500 text-primary-blue-500 rounded-md ml-3"
            >
              Cancel
            </button>
          </div>
        }
        modalSize="w-10/12 md:w-5/12 3xl:w-3/12"
      >
        <div className="mt-7 text-lg leading-8 text-gray-600 ">
          <div className="relative mt-2 rounded-md shadow-sm">
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              name="account-number"
              id="account-number"
              className="text-sm font-regular border  
                    rounded-md p-2 w-full
                    focus:border-primary-blue-500"
              placeholder="Reset Password"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {show ? (
                <span onClick={() => setShow(!show)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 
                          0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </span>
              ) : (
                <span onClick={() => setShow(!show)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 
                          12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 
                          4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 
                          6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                    />
                  </svg>
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 password-checklist">
            <ReactPasswordChecklist
              rules={['minLength', 'specialChar', 'number', 'capital']}
              minLength={8}
              value={password}
              specialCharsRegex={
                /^(?=.*[!@#$%^&*()\-=_+[\]{};':"\\|,.<>?])(?!.*(1234|aaaa|abcd)).*$/g
              }
              messages={{
                specialChar:
                  'Password should contain special character and does not contain any sequences',
              }}
            />
          </div>

          <div className="text-center"></div>
        </div>
      </CustomModal>
    </>
  );
};

export default PatientEdit;
